import { TONE } from './feedback';
import type { Tone } from './feedback';
import { RCU_HTTP_LIMITS } from './protocol';

/**
 * How the console reaches an RCU.
 *
 * Two channels, for two different jobs:
 *
 * - **MQTT** carries anything with a reply. The firmware answers commands on
 *   `<serial>/toCloudRCU`, streams logs on `<serial>/logRCU` and coredump chunks
 *   on `<serial>/coredumpRCU`; a browser can subscribe to all three when the
 *   installation's broker exposes a WebSocket listener.
 * - **HTTP** (`POST /command` on port 8900) is the guest path: no broker, no
 *   session, delivery acknowledged but never confirmed. See `../transport`.
 *
 * Field and topic spelling here is taken from the RCU HTTP/MQTT command
 * reference (branch 4.2.0, commit 3986962).
 */

/** Connection state of the broker link, as the console's header chip shows it. */
export const CONNECTION_STATUS = {
  /** No broker configured for this tablet yet. */
  IDLE: 'idle',
  CONNECTING: 'connecting',
  ONLINE: 'online',
  /** Dropped and retrying; mqtt.js owns the backoff. */
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
} as const;

export type ConnectionStatus = (typeof CONNECTION_STATUS)[keyof typeof CONNECTION_STATUS];

/** Topic roles. Every topic is `<serial>` plus one of these suffixes. */
export const TOPIC_ROLE = {
  /** Commands, client to RCU. Never retained. */
  COMMANDS: 'commands',
  /** Acks, mapping reads and retained online status, RCU to client. */
  EVENTS: 'events',
  /** Raw diagnostic text, RCU to client. */
  LOGS: 'logs',
  /** Base64 coredump chunks, RCU to client. */
  COREDUMP: 'coredump',
} as const;

export type TopicRole = (typeof TOPIC_ROLE)[keyof typeof TOPIC_ROLE];

export const TOPIC_SUFFIX = {
  [TOPIC_ROLE.COMMANDS]: '/toDeviceRCU',
  [TOPIC_ROLE.EVENTS]: '/toCloudRCU',
  [TOPIC_ROLE.LOGS]: '/logRCU',
  [TOPIC_ROLE.COREDUMP]: '/coredumpRCU',
} as const satisfies Record<TopicRole, string>;

/**
 * QoS per role.
 *
 * The events topic carries both the ack firehose (published at 0) and documents
 * the firmware publishes at 1 — mapping reads and the retained lifecycle status —
 * so the console subscribes at 1: a subscription is capped by the publish QoS,
 * never raised, so this loses none of the firehose and keeps the documents that
 * would otherwise be dropped. The log stream is genuinely lossy and stays at 0,
 * and coredump chunks and commands are 1 because neither tolerates loss. QoS 1
 * can deliver duplicates, so consumers of the coredump topic deduplicate by
 * `dumpid`/`upload`/`seq`.
 */
export const MQTT_QOS = {
  [TOPIC_ROLE.EVENTS]: 1,
  [TOPIC_ROLE.LOGS]: 0,
  [TOPIC_ROLE.COREDUMP]: 1,
  [TOPIC_ROLE.COMMANDS]: 1,
} as const satisfies Record<TopicRole, number>;

/** Memory bars warn as they fill: amber past `WARN`, red past `CRITICAL`. */
export const MEMORY_PRESSURE = {
  WARN_PERCENT: 75,
  CRITICAL_PERCENT: 90,
  FULL_PERCENT: 100,
} as const;

/** How many recent events a panel lists when it shows a tail of the feed. */
export const PANEL_EVENT_TAIL = 8;

/** How a link state reads in the header chip. */
export const CONNECTION_LABEL = {
  [CONNECTION_STATUS.IDLE]: 'Offline',
  [CONNECTION_STATUS.CONNECTING]: 'Connecting',
  [CONNECTION_STATUS.ONLINE]: 'Live',
  [CONNECTION_STATUS.RECONNECTING]: 'Reconnecting',
  [CONNECTION_STATUS.ERROR]: 'Link error',
} as const satisfies Record<ConnectionStatus, string>;

export const CONNECTION_TONE = {
  [CONNECTION_STATUS.IDLE]: TONE.NEUTRAL,
  [CONNECTION_STATUS.CONNECTING]: TONE.INFO,
  [CONNECTION_STATUS.ONLINE]: TONE.SUCCESS,
  [CONNECTION_STATUS.RECONNECTING]: TONE.WARNING,
  [CONNECTION_STATUS.ERROR]: TONE.ERROR,
} as const satisfies Record<ConnectionStatus, Tone>;

export const connectionLabel = (status: ConnectionStatus): string => CONNECTION_LABEL[status];

export const connectionTone = (status: ConnectionStatus): Tone => CONNECTION_TONE[status];

/** Which transport carried a command, for the event feed and the reply chip. */
/** Which route the live session came up on. There is one MQTT route. */
export const LINK_ROUTE = {
  /** The gateway, which holds the broker connection a browser cannot open. */
  BRIDGE: 'bridge',
  /** No session is up. */
  NONE: 'none',
} as const;

export type LinkRoute = (typeof LINK_ROUTE)[keyof typeof LINK_ROUTE];

export const SEND_TRANSPORT = {
  /** Straight to the broker's own WebSocket listener. */
  MQTT: 'mqtt',
  /**
   * Published through the bridge gateway, which holds the TCP connection a
   * browser cannot. Its own value, because a technician proving a path needs to
   * see which one carried the command.
   */
  BRIDGE: 'bridge',
  HTTP: 'http',
  /** Neither was possible: no broker session and nowhere to POST. */
  NONE: 'none',
} as const;

export type SendTransport = (typeof SEND_TRANSPORT)[keyof typeof SEND_TRANSPORT];

export const SEND_TRANSPORT_LABEL = {
  [SEND_TRANSPORT.MQTT]: 'Published',
  [SEND_TRANSPORT.BRIDGE]: 'Published via the bridge',
  [SEND_TRANSPORT.HTTP]: 'HTTP',
  [SEND_TRANSPORT.NONE]: 'Not sent',
} as const satisfies Record<SendTransport, string>;

/**
 * How this tablet decides which channel a command leaves on. One choice, obeyed
 * by the technician console and by the guest room controls alike, so what a
 * technician sees on the bench is what a guest gets.
 */
export const TRANSPORT_PREFERENCE = {
  /** MQTT while a session is live, HTTP otherwise. The default. */
  AUTOMATIC: 'automatic',
  /** Only the broker: a command without a session fails loudly, never silently moves. */
  MQTT: SEND_TRANSPORT.MQTT,
  /** Only `POST /command`, even with a broker up — for comparing the two paths. */
  HTTP: SEND_TRANSPORT.HTTP,
} as const;

export type TransportPreference =
  (typeof TRANSPORT_PREFERENCE)[keyof typeof TRANSPORT_PREFERENCE];

/** Declaration order, for the picker and for exhaustiveness. */
export const TRANSPORT_PREFERENCE_KEYS = [
  TRANSPORT_PREFERENCE.AUTOMATIC,
  TRANSPORT_PREFERENCE.MQTT,
  TRANSPORT_PREFERENCE.HTTP,
] as const satisfies readonly TransportPreference[];

export const TRANSPORT_PREFERENCE_LABEL = {
  [TRANSPORT_PREFERENCE.AUTOMATIC]: 'Automatic',
  [TRANSPORT_PREFERENCE.MQTT]: 'MQTT',
  [TRANSPORT_PREFERENCE.HTTP]: 'HTTP',
} as const satisfies Record<TransportPreference, string>;

export const TRANSPORT_PREFERENCE_HINT = {
  [TRANSPORT_PREFERENCE.AUTOMATIC]: 'MQTT while the broker is live, HTTP when it is not.',
  [TRANSPORT_PREFERENCE.MQTT]:
    'Only the broker. Commands fail rather than falling back, so a reply is always expected.',
  [TRANSPORT_PREFERENCE.HTTP]:
    'Only POST /command. Delivered, never confirmed — state still comes from the broker.',
} as const satisfies Record<TransportPreference, string>;

/** Reads a stored preference, falling back to automatic for anything unknown. */
export const transportPreferenceFrom = (value: unknown): TransportPreference =>
  typeof value === 'string' &&
  (TRANSPORT_PREFERENCE_KEYS as readonly string[]).includes(value)
    ? (value as TransportPreference)
    : TRANSPORT_PREFERENCE.AUTOMATIC;

/**
 * The snapshot taken when the link comes up — once, never a poll.
 *
 * Reads are spread out because the firmware's command queue holds 20 messages
 * and drops what it cannot publish within 100 ms: a rack of 60 modules asked at
 * once would lose most of its answers.
 */
export const LINK_HYDRATION = {
  SPACING_MS: 250,
  /** How long a command may wait in the firmware's queue before it is dropped. */
  QUEUE_DROP_MS: 100,
} as const;

/** Which way a message travelled, as the event drawer groups it. */
export const EVENT_DIRECTION = {
  OUT: 'out',
  IN: 'in',
  /** The link itself: connecting, dropping, reconnecting. */
  LINK: 'link',
} as const;

export type EventDirection = (typeof EVENT_DIRECTION)[keyof typeof EVENT_DIRECTION];

/** Words a switch reads by in the console, so no component spells them itself. */
export const TOGGLE_WORD = {
  ON: 'on',
  OFF: 'off',
} as const;

export const toggleWord = (value: boolean): string => (value ? TOGGLE_WORD.ON : TOGGLE_WORD.OFF);

/** What the event drawer's filter chips select. */
export const FEED_FILTER = {
  ALL: 'all',
  OUT: 'out',
  IN: 'in',
  /** Anything that failed: bad acks, refused commands, unreadable messages. */
  ERRORS: 'errors',
} as const;

export type FeedFilter = (typeof FEED_FILTER)[keyof typeof FEED_FILTER];

export const FEED_FILTER_LABEL = {
  [FEED_FILTER.ALL]: 'All',
  [FEED_FILTER.OUT]: 'Sent',
  [FEED_FILTER.IN]: 'Replies',
  [FEED_FILTER.ERRORS]: 'Problems',
} as const satisfies Record<FeedFilter, string>;

/** Broker address pieces. Any port is allowed; these are the usual WebSocket ones. */
export const BROKER_SCHEME = {
  /** Plain WebSocket. Blocked when the app itself is served over HTTPS. */
  PLAIN: 'ws',
  TLS: 'wss',
} as const;

export const BROKER_DEFAULT = {
  SCHEME: BROKER_SCHEME.PLAIN,
  /** Mosquitto's convention for `protocol websockets`. */
  PORT: 9001,
  /** TLS variant, when the broker is configured for it. */
  TLS_PORT: 9002,
} as const;

/**
 * What the gateway answers on. It is a Node process that holds the broker
 * connection a browser cannot open, and lends it over HTTP and WebSocket for a
 * JWT. See `~/projects/mqtt-bridge`.
 */
export const BRIDGE_DEFAULT = {
  LOGIN_PATH: '/api/auth/login',
  STREAM_PATH: '/api/mqtt/stream',
  HEALTH_PATH: '/health',
  /** A WebSocket handshake carries no headers, so the token rides in the URL. */
  TOKEN_QUERY: 'token',
} as const;

/**
 * The gateway's frame types, as its own protocol spells them. Only the ones this
 * client acts on are named: `subscribed`, `published` and `buffered` are
 * confirmations, and a frame it does not recognise is a gateway newer than this
 * build rather than an error.
 */
export const BRIDGE_FRAME = {
  /** Sent once the gateway has attached this client to its upstream connection. */
  READY: 'ready',
  /** One message from a subscribed topic. */
  MESSAGE: 'message',
  /** The gateway refused something, by code and by sentence. */
  ERROR: 'error',
} as const;

/**
 * The bridge this installation falls back to: its account, and no address.
 *
 * The address is deliberately empty. Whichever host serves the app is expected
 * to serve the gateway on the same origin, so the app reads its own — see
 * `pageOrigin` — and the same build works on a tunnel, on a VPS, or on a machine
 * in the hotel, with nothing to rebuild when the address changes. A technician
 * who needs a gateway somewhere else types one into the link sheet.
 */
export const HOUSE_BRIDGE = {
  URL: '',
  USERNAME: 'toyama-bridge',
  PASSWORD: 'UqDLQpWza!-sF5BjjkskNV',
} as const;

/**
 * Timings and caps the link runs with. Acks are also broadcast to UDP
 * `255.255.255.255:56000`, which a browser cannot listen to, so the console
 * works from the MQTT topics only.
 */
export const LINK_LIMITS = {
  KEEPALIVE_SECONDS: 30,
  RECONNECT_MS: 4_000,
  CONNECT_TIMEOUT_MS: 8_000,
  /**
   * MQTT is not limited by the firmware's HTTP buffer, but a body that fits can
   * be sent either way, so the console holds every payload to this.
   */
  MAX_COMMAND_BYTES: RCU_HTTP_LIMITS.MAX_COMMAND_BYTES,
  /** Events kept in the drawer. Older ones fall off; the count is reported. */
  EVENT_RING: 500,
  /** Log lines kept for the tail view. */
  LOG_RING: 2_000,
  /**
   * How long a broker setting must sit still before the app reconnects. The
   * settings form saves as the technician types, and a socket per keystroke
   * would spend the session reconnecting instead of carrying commands.
   */
  SETTINGS_SETTLE_MS: 600,
} as const;

/** Base the generated client id is rendered in: hexadecimal slices of the clock. */
export const CLIENT_ID_RADIX = 16;
/** Characters of a generated client id, after the prefix. */
export const CLIENT_ID_LENGTH = 8;

/**
 * WebSocket events the gateway client listens to, by their DOM names. They are
 * named here for the same reason `MQTT_EVENT` is: `error` is also an issue
 * severity, and a raw literal would collide with it.
 */
export const SOCKET_EVENT = {
  OPEN: 'open',
  MESSAGE: 'message',
  CLOSE: 'close',
  ERROR: 'error',
} as const;
